import React, { useEffect, useRef } from "react"

let GAUGE: number;

function getTargetCellSize(width: number) {
    if (width < 640) {
        GAUGE = 2;
        return 20
    }

    if (width < 1024) {
        GAUGE = 2
        return 40
    }

    GAUGE = 4
    return 60
}

type Cell = {
    x: number
    y: number
    size: number
    active: boolean
    startTime: number
    duration: number
    delay: number
    color: string
    gauge: number
    scale: number
}

export default function XPatternBackground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const cellsRef = useRef<Cell[]>([])
  const candidatesRef = useRef<number[]>([])
  const rafRef = useRef<number | null>(null)

  const gridRef = useRef({ rows: 0, cols: 0, cell: 20 })
  const lastRevealRef = useRef(0)

  const mouseRef = useRef({ x: -1, y: -1 })

  // ---------- BUILD GRID ----------
  const build = () => {

    const JAGGEDNESS = 0.8
    const FREQUENCY = 10
    const VERTICALITY = 0.8

    const canvas = canvasRef.current
    if (!canvas) return

    const w = window.innerWidth
    const h = window.innerHeight

    canvas.width = w
    canvas.height = h

    const cellTarget = getTargetCellSize(w)
    const cols = Math.ceil(w / cellTarget)
    const cell = w / cols
    const rows = Math.ceil(h / cell)

    gridRef.current = { rows, cols, cell }

    const cells: Cell[] = []
    const candidates: number[] = []

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = c * cell
        const y = r * cell

        const nx = c / cols
        const ny = r / rows

        const raw = Math.sin((nx * VERTICALITY + ny * (1 - VERTICALITY)) * Math.PI * FREQUENCY)
        const wave = Math.sign(raw) * Math.pow(Math.abs(raw), 0.5)
        const allowed = wave > JAGGEDNESS

        const idx = r * cols + c
        const specialX = Math.random() > 0.7

        if (allowed) candidates.push(idx)

        cells.push({
            x,
            y,
            size: cell,
            active: false,
            duration: 0.6 + Math.random() * 1.3,
            delay: Math.random() * 0.1,
            progress: 0,
            startTime: 0,
            color: specialX ? "#75231D" : "#510A0B",
            gauge: specialX ? GAUGE + 1 : GAUGE,
            scale: 1
        })
      }
    }

    cellsRef.current = cells
    candidatesRef.current = candidates
  }

  // ---------- DRAW X ----------
  const drawX = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    p1: number,
    p2: number,
    color: string,
    gauge: number,
    scale: number
  ) => {
    const cx = x + size / 2
    const cy = y + size / 2

    ctx.save()
    ctx.translate(cx, cy)
    ctx.scale(scale, scale)
    ctx.translate(-cx, -cy)

    const pad = size * 0.2
  
    const x1 = x + pad
    const y1 = y + pad
    const x2 = x + size - pad
    const y2 = y + size - pad
  
    ctx.strokeStyle = color
    ctx.lineWidth = gauge
    ctx.lineCap = "round"
  
    if (p1 > 0) {
      ctx.beginPath()
      ctx.moveTo(x1, y1)
      ctx.lineTo(
        x1 + (x2 - x1) * p1,
        y1 + (y2 - y1) * p1
      )
      ctx.stroke()
    }
  
    if (p2 > 0.03) {
      ctx.beginPath()
      ctx.moveTo(x2, y1)
      ctx.lineTo(
        x2 - (x2 - x1) * p2,
        y1 + (y2 - y1) * p2
      )
      ctx.stroke()
    }

    ctx.restore()
  }

  // ---------- ANIMATION LOOP ----------
  const animate = (t: number) => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext("2d")
    if (!ctx || !canvas) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const cells = cellsRef.current
    const candidates = candidatesRef.current

    if (
      candidates.length &&
      t - lastRevealRef.current > 180
    ) {
      const idx = Math.floor(Math.random() * candidates.length)
      const cellIndex = candidates[idx]

      const cell = cells[cellIndex]
      cell.active = true
      cell.startTime = t

      candidates[idx] = candidates[candidates.length - 1]
      candidates.pop()

      lastRevealRef.current = t
    }

    const mouse = mouseRef.current

    // detect directly hovered cell
    let hoveredCell: Cell | null = null

    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i]
      if (!cell.active) continue

      if (
        mouse.x >= cell.x &&
        mouse.x <= cell.x + cell.size &&
        mouse.y >= cell.y &&
        mouse.y <= cell.y + cell.size
      ) {
        hoveredCell = cell
        break
      }
    }

    const hoveredIsSpecial = hoveredCell?.color === "#75231D"

    for (let i = 0; i < cells.length; i++) {
        const cell = cells[i]
        if (!cell.active) continue
      
        const elapsed = t - cell.startTime
        const d = cell.duration * 150
      
        const p1 = Math.min(1, elapsed / d)
      
        const SECOND_DELAY = d * 1.7
        const SECOND_RAMP = d * 0.9
      
        let p2 = 0
      
        if (elapsed > SECOND_DELAY) {
          p2 = Math.min(
            1,
            (elapsed - SECOND_DELAY) / SECOND_RAMP
          )
        }

        const cx = cell.x + cell.size / 2
        const cy = cell.y + cell.size / 2

        const dx = mouse.x - cx
        const dy = mouse.y - cy
        const dist = Math.sqrt(dx * dx + dy * dy)

        const influenceRadius = cell.size * 2.5

        let targetScale = 1

        if (hoveredCell) {
          if (cell === hoveredCell) {
            targetScale = hoveredIsSpecial ? 1.4 : 1.3
          } else if (dist < influenceRadius) {
            const falloff = 1 - dist / influenceRadius
            const adjacentBase = hoveredIsSpecial ? 1.3 : 1.2
            targetScale = 1 + (adjacentBase - 1) * falloff
          }
        }

        // smooth easing
        cell.scale += (targetScale - cell.scale) * 0.15
      
        drawX(
          ctx,
          cell.x,
          cell.y,
          cell.size,
          p1,
          p2,
          cell.color,
          cell.gauge,
          cell.scale
        )
      }

    rafRef.current = requestAnimationFrame(animate)
  }

  // ---------- INIT ----------
  useEffect(() => {
    build()
    window.addEventListener("resize", build)

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX
      mouseRef.current.y = e.clientY
    }

    window.addEventListener("mousemove", handleMouseMove)

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      window.removeEventListener("resize", build)
      window.removeEventListener("mousemove", handleMouseMove)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        background: "transparent",
        zIndex: -1
      }}
    />
  )
}